/* eslint-disable import/order */
import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { boardEntryPoint } from '@jira/spa-apps__board/src/boardEntryPoint.entrypoint.tsx';
import {
	boardFilterRefinementResource,
	conditionalBoardTmpMatureExampleGupsResource,
} from '@jira/router-resources__board-resource/src/conditionalBoardTmpMatureExampleGupsResource.tsx';
import { aiEntitlementResource } from '@jira/router-resources__ai-entitlement/src/aiEntitlementResource.tsx';
import { coloredLabelsResource } from '@jira/router-resources__colored-labels/src/coloredLabelsResource.tsx';
import { TEAM_MANAGED_BOARD } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { forgeBoardModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { softwareBoardRoute } from '@jira/router-routes__software-board-routes/src/softwareBoardRoute.tsx';
import { NextGenBoardSkeleton } from '@jira/platform__skeletons/src/ui/next-gen-board/NextGenBoardSkeleton.tsx';
import { ThemedLazySkeleton } from '@jira/platform__project-theme-components/src/ui/ThemedLazySkeleton.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { softwareProjectLayout } from '@jira/platform__layouts-software-project-layout/src/index.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { jiraHorizontalNavEntrypoint } from '@jira/navigation-apps__horizontal-nav-jira/src/jiraHorizontalNavEntrypoint.entrypoint.tsx';
import { conditionalBoardNBTOptInResource } from './common/conditionalBoardNBTOptInResource.tsx';
import { conditionalBoardRelayResource } from './common/conditionalBoardRelayResource.tsx';
import { conditionalBoardResource } from './common/conditionalBoardResource.tsx';
import { softwareBoardPageEntrypoint } from './ui/index.tsx';
import type { Route } from '@atlassian/react-resource-router';

const boardPageEntryPoint = createPageEntryPoint({
	main: boardEntryPoint,
	horizontalNavigation: jiraHorizontalNavEntrypoint,
});

const getColoredLabelsResource = () => {
	return expVal(
		'adding-color-to-labels-field-experiment-milestone2',
		'isColorfulNewLabelsEnabled',
		false,
	)
		? [coloredLabelsResource]
		: [];
};

const getLegacyResources = () => {
	if (expValEquals('board_harmonisation_software_tmp', 'isEnabled', true, false)) {
		return [projectThemeResource];
	}

	return [
		projectContextResource,
		staticProjectSidebarNavigationResource,
		conditionalBoardResource,
		boardFilterRefinementResource,
		conditionalBoardTmpMatureExampleGupsResource,
		conditionalBoardRelayResource,
		conditionalBoardNBTOptInResource,
		forgeBoardModuleResource,
		projectThemeResource,
		resourceWithCondition(() => fg('ai_sprint_goal_gate'), aiEntitlementResource),
		...getColoredLabelsResource(),
	];
};

export const softwareBoardRouteEntry: Route = createEntry(softwareBoardRoute, () => ({
	group: 'software',
	skeleton: expValEquals('board_harmonisation_software_tmp', 'isEnabled', true, false)
		? ThemedLazySkeleton
		: NextGenBoardSkeleton,

	layout: composeLayouts(
		softwareProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				query: ['selectedIssue'],
			},
		]),
	),

	navigation: {
		menuId: MenuIdProjects,
	},

	resources: [...getNavigationResources(), ...getIssueViewResources(), ...getLegacyResources()],

	preloadOptions: {
		earlyChunks: expValEquals('board_harmonisation_software_tmp', 'isEnabled', true, false)
			? []
			: ['software-board-early-entry'],
	},

	afterPaint: [LazyIssueApp],
	entryPoint: () =>
		expValEquals('board_harmonisation_software_tmp', 'isEnabled', true, false)
			? boardPageEntryPoint
			: softwareBoardPageEntrypoint,
	ufoName: 'next-gen-board',

	meta: {
		capability: TEAM_MANAGED_BOARD,
	},

	supportsPreviewPanel: true,
	supportsProjectTheme: 'full',
	isHydrateRootEnabled: true,

	bifrost: {
		bifrostGroup: 'backlog-and-board',
	},
}));
